import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Center,
  Stack,
  Spinner
} from '@chakra-ui/react';
import GroupingModel from 'models/grouping/GroupingModel';
import { ArtistModel, UserModel, PaginationModel } from 'models';
import { ProcessStatus } from 'models/types';
import DetailedGroupingCard from '../DetailedGroupingCard/DetailedGroupingCard';
import GroupingFiltersForm from '../../Form/GroupingFiltersForm/GroupingFiltersForm';
import EmptyState from '../../EmptyState/EmptyState';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';
import { filterItemsByQuery } from 'utils/search';

const PER_PAGE = 10;

export interface DetailedGroupingGridProps {
  groupings: GroupingModel[];
  isLoading: boolean;
  onApprove?: (groupingId: string) => void;
  onReject?: (groupingId: string) => void;
  onRemoveListing?: (groupingId: string, listingId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  pagination?: PaginationModel<GroupingModel>;
  onPageChange?: (page: number) => void;
  defaultStatus?: ProcessStatus;
  onStatusChange?: (status: ProcessStatus) => void;
  defaultAccordionOpen?: boolean;
  availableArtists?: ArtistModel[];
  availableAdminUsers?: UserModel[];
  selectionMode?: boolean;
  selectedGroupings?: GroupingModel[];
  onGroupingSelect?: (grouping: GroupingModel) => void;
  onFilteredGroupingsChange?: (groupings: GroupingModel[]) => void;
}

export default function DetailedGroupingGrid({
  groupings,
  isLoading,
  onApprove,
  onReject,
  onRemoveListing,
  approveLoading = false,
  rejectLoading = false,
  pagination,
  onPageChange,
  defaultStatus = ProcessStatus.PENDING,
  onStatusChange,
  defaultAccordionOpen = false,
  availableArtists = [],
  availableAdminUsers = [],
  selectionMode = false,
  selectedGroupings = [],
  onGroupingSelect,
  onFilteredGroupingsChange
}: DetailedGroupingGridProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>([]);
  const [selectedCreatedByUsers, setSelectedCreatedByUsers] = useState<UserModel[]>([]);
  const [page, setPage] = useState(1);

  const isOnFilterMode = (): boolean => {
    return searchQuery.length > 0 || selectedArtists.length > 0 || selectedCreatedByUsers.length > 0;
  };

  const filteredGroupings = useMemo(() => {
    let filtered = groupings;

    // Filter by artists
    if (selectedArtists.length > 0) {
      const artistIds = new Set(selectedArtists.map(artist => artist.id));
      filtered = filtered.filter(grouping => {
        return Array.from(artistIds).some(artistId => grouping.containsArtist(artistId));
      });
    }

    // Filter by created_by users
    if (selectedCreatedByUsers.length > 0) {
      const userIds = new Set(selectedCreatedByUsers.map(user => user.id));
      filtered = filtered.filter(grouping => {
        return grouping.createdBy && userIds.has(grouping.createdBy.id);
      });
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      // 1) Collect the items from filtered groupings
      const items = filtered.map(g => g.item);

      // 2) Reuse your util to filter items
      const filteredItems = filterItemsByQuery(items, searchQuery);

      // 3) Build a quick lookup (by stable key) and filter groupings
      const allowedIds = new Set(filteredItems.map(item => item.id));
      filtered = filtered.filter(g => allowedIds.has(g.item.id));
    }

    return filtered;
  }, [groupings, searchQuery, selectedArtists, selectedCreatedByUsers]);

  // Memoize visible groupings: use server pagination when not filtering
  const paginatedGroupings = useMemo(() => {
    if (!isOnFilterMode() && pagination) {
      return filteredGroupings;
    }
    const startIdx = (page - 1) * PER_PAGE;
    const endIdx = startIdx + PER_PAGE;
    return filteredGroupings.slice(startIdx, endIdx);
  }, [filteredGroupings, page, pagination, searchQuery, selectedArtists, selectedCreatedByUsers]);

  // Notify parent of filtered groupings changes (for Select All functionality)
  useEffect(() => {
    onFilteredGroupingsChange?.(paginatedGroupings);
  }, [paginatedGroupings, onFilteredGroupingsChange]);


  // Calculate total pages based on filter mode vs server mode
  const filteredTotalPages = Math.ceil(filteredGroupings.length / PER_PAGE);
  const totalPages = !isOnFilterMode() && pagination?.totalPages
    ? pagination.totalPages
    : filteredTotalPages;
  const currentPage = !isOnFilterMode() && pagination?.currentPage
    ? pagination.currentPage
    : page;

  const handlePageChange = (newPage: number) => {
    if (!isOnFilterMode() && onPageChange) {
      onPageChange(newPage);
      return;
    }
    setPage(newPage);
  };

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1); // Reset to first page when status changes
    onStatusChange?.(status);
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    setPage(1); // Reset to first page when artists change
  };

  const handleCreatedByChange = (users: UserModel[]) => {
    setSelectedCreatedByUsers(users);
    setPage(1); // Reset to first page when created_by changes
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedArtists([]);
    setSelectedCreatedByUsers([]);
    setPage(1);
  };

  const renderGroupingCards = () => {
    if (isLoading) {
      return (
        <Center p={8}>
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!filteredGroupings?.length) {
      return (
        <EmptyState
          header="No Groupings"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return (
      <Stack spacing={4}>
        {paginatedGroupings.map(grouping => (
          <DetailedGroupingCard
            key={`grouping_card_${grouping.id}`}
            grouping={grouping}
            onApprove={onApprove}
            onReject={onReject}
            onRemoveListing={onRemoveListing}
            approveLoading={approveLoading}
            rejectLoading={rejectLoading}
            defaultAccordionOpen={defaultAccordionOpen}
            selectionMode={selectionMode}
            selected={selectedGroupings.some(g => g.id === grouping.id)}
            onSelect={() => onGroupingSelect?.(grouping)}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box width="100%">
      <Box mb={6}>
        <GroupingFiltersForm
          defaultStatus={selectedStatus}
          defaultQuery={searchQuery}
          defaultArtists={selectedArtists}
          defaultCreatedByUsers={selectedCreatedByUsers}
          availableArtists={availableArtists}
          availableAdminUsers={availableAdminUsers}
          onStatusChange={handleStatusChange}
          onQueryChange={handleQueryChange}
          onArtistChange={handleArtistChange}
          onCreatedByChange={handleCreatedByChange}
        />
      </Box>
      {renderGroupingCards()}
      {totalPages > 1 && (
        <Box mt={8}>
          <PaginationFooter
            currentPage={currentPage}
            lastPage={totalPages}
            siblingsCount={1}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}
